import type { IncidentAction } from "domains/incidents/constants/actionTypes";
import { IncidentTypes } from "domains/incidents/constants/actionTypes";
import type { IncidentComponent, IncidentState } from "domains/incidents/types";

export const initialState: IncidentState = { dismissed: [], unresolved: [] };

const isAcceptableImpact = (impact?: string) => impact && ["major", "critical"].includes(impact.toLowerCase());

const impactSeverity: Partial<Record<string, number>> = {
  major: 1,
  critical: 2,
};

const isAcceptableComponents = (components: IncidentComponent[]) =>
  components.some(
    ({ id }) =>
      id &&
      [
        import.meta.env.VITE_STRATEGIC_INTELLIGENCE_STATUS_PAGE_ID,
        import.meta.env.VITE_PAYMENT_PORTAL_STATUS_PAGE_ID,
      ].includes(id),
  );

export const incidentsReducer = (state: IncidentState = initialState, action: IncidentAction): IncidentState => {
  switch (action.type) {
    case IncidentTypes.FETCH_UNRESOLVED_SUCCESS: {
      const acceptedIncidents = action.payload
        .filter(
          ({ impact, impact_override, components }) =>
            isAcceptableImpact(impact_override ?? impact) && isAcceptableComponents(components),
        )
        .map(({ updated_at, incident_updates, impact, impact_override, ...rest }) => ({
          ...rest,
          impact: (impact_override ?? impact).toLowerCase(),
          updatedAt: new Date(updated_at).getTime() / 1000,
          incidentUpdates: incident_updates.map(({ display_at, ...update }) => ({
            ...update,
            displayAt: new Date(display_at).getTime() / 1000,
          })),
        }));
      acceptedIncidents.sort((inc1, inc2) => {
        const severityDiff = (impactSeverity[inc2.impact] ?? 0) - (impactSeverity[inc1.impact] ?? 0);
        return severityDiff !== 0 ? severityDiff : inc2.updatedAt - inc1.updatedAt;
      });
      const acceptedIncidentIds = acceptedIncidents.map(({ id }) => id);
      return {
        ...state,
        dismissed: state.dismissed.filter((dismissedId) => acceptedIncidentIds.includes(dismissedId)),
        unresolved: acceptedIncidents,
      };
    }
    case IncidentTypes.DISMISS_INCIDENT:
      return {
        ...state,
        dismissed: [...state.dismissed, action.payload.id],
      };
    default:
      return state;
  }
};
